import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, Plus, Search, MapPin, LayoutGrid, Table2, Pencil, Trash2 } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Card } from "src/components/ui/card"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useProjects, useDeleteProject } from "src/features/projects/hooks/useProjects"
import { assignProject } from "src/features/projects/api/projectsApi"
import { ProjectDrawer } from "src/features/projects/components/ProjectDrawer"
import { DeleteProjectModal } from "src/features/projects/components/DeleteProjectModal"
import { ProjectsTable } from "src/features/projects/components/ProjectsTable"
import { AssigneePicker } from "src/features/projects/components/AssigneePicker"
import { UNIT_STATUS_STYLES } from "src/features/projects/components/UnitStatusBadge"
import {
  PROJECT_TYPES,
  PROJECT_TYPE_MAP,
  PROJECT_STATUS_MAP,
  LIGHT_CARD,
} from "src/features/projects/constants"

const TYPE_FILTERS = [{ value: "", label: "All" }, ...PROJECT_TYPES]
const ACTIVE_FILTERS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
]

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function FilterChips({ label, options, value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
      {options.map((o) => {
        const selected = value === o.value
        return (
          <button
            key={o.value || "all"}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={selected}
            className={[
              "rounded-full border px-3 py-1 text-[13px] font-medium transition-colors",
              selected
                ? "border-slate-800 bg-slate-800 text-white"
                : "border-slate-200 text-slate-600 hover:bg-slate-50",
            ].join(" ")}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Card view (kept as an alternative to the table) ──────────────────────────
function AvailabilityBar({ counts = {}, total = 0 }) {
  if (!total) return <p className="text-[13px] text-slate-400">No units added yet</p>
  const order = ["available", "blocked", "booked", "sold"]
  return (
    <div className="space-y-2">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
        {order.map((s) => {
          const n = counts[s] || 0
          if (!n) return null
          return (
            <div
              key={s}
              style={{ width: `${(n / total) * 100}%`, backgroundColor: UNIT_STATUS_STYLES[s].swatchColor }}
              title={`${UNIT_STATUS_STYLES[s].label}: ${n}`}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {order.map((s) => (
          <span key={s} className="flex items-center gap-1.5 text-[12px] text-slate-500">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: UNIT_STATUS_STYLES[s].swatchColor }} />
            {UNIT_STATUS_STYLES[s].label} {counts[s] || 0}
          </span>
        ))}
      </div>
    </div>
  )
}

function ProjectCard({ project, onView, onEdit, onDelete, onAssign, assigning }) {
  const type = PROJECT_TYPE_MAP[project.project_type]
  const status = PROJECT_STATUS_MAP[project.status]
  return (
    <Card className={`flex flex-col gap-4 p-5 transition-shadow hover:shadow-sm ${LIGHT_CARD}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <h3 className="truncate text-[15px] font-semibold text-slate-900">{project.name}</h3>
          <p className="mt-0.5 flex items-center gap-1 text-[12px] text-slate-500">
            {project.code && <span className="font-mono uppercase">{project.code}</span>}
            {project.code && (project.city || project.state) && <span>·</span>}
            {(project.city || project.state) && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {[project.city, project.state].filter(Boolean).join(", ")}
              </span>
            )}
          </p>
        </div>
        {type && (
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${type.badge}`}>
            {type.label}
          </span>
        )}
      </div>

      <AvailabilityBar counts={project.unit_status_counts} total={project.units_total} />

      <div className="flex items-center justify-between text-[12px]">
        {status && (
          <span className="inline-flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: status.dotColor }} />
            {status.label}
          </span>
        )}
        <AssigneePicker
          assignedTo={project.assigned_to}
          isPending={assigning}
          onAssign={(membershipId) => onAssign(project, membershipId)}
          compact
        />
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-[12px] text-slate-400">
          {project.units_total || 0} unit{project.units_total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => onEdit(project)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-slate-500 hover:text-red-600"
            onClick={() => onDelete(project)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onView(project)}>
            <LayoutGrid className="h-3.5 w-3.5" />
            View units
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [searchRaw, setSearch] = useState("")
  const [projectType, setProjectType] = useState("")
  const [active, setActive] = useState("")
  const [view, setView] = useState("table")
  const q = useDebounce(searchRaw)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [assigningId, setAssigningId] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const { data, loading, fetch } = useProjects()
  const deleteHook = useDeleteProject()

  const currentFilters = useCallback(
    () => ({ page: 1, per_page: 50, q, project_type: projectType, active }),
    [q, projectType, active]
  )

  useEffect(() => { fetch(currentFilters()) }, [q, projectType, active])

  function openCreate() { setEditing(null); setDrawerOpen(true) }
  function openEdit(project) { setEditing(project); setDrawerOpen(true) }
  function handleView(project) { navigate(`/projects/${project.id}`) }
  function handleSuccess() { fetch(currentFilters()) }

  function requestDelete(project) { setDeleting(project) }

  async function confirmDelete(id) {
    try {
      await deleteHook.mutate(id)
      toast({ title: "Project deleted", description: deleting?.name })
      setDeleting(null)
      fetch(currentFilters())
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not delete project"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  async function handleAssign(project, membershipId) {
    setAssigningId(project.id)
    try {
      await assignProject(project.id, membershipId)
      toast({
        title: membershipId ? "Project assigned" : "Project unassigned",
        description: project.name,
      })
      fetch(currentFilters())
    } catch (err) {
      const msg = err.response?.data?.errors?.[0] || "Could not update assignee"
      toast({ variant: "destructive", title: "Error", description: msg })
    } finally {
      setAssigningId(null)
    }
  }

  const projects = data?.projects ?? []
  const total = data?.meta?.total

  return (
    <AppShell>
      <div className="space-y-5 px-8 py-7">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Building2 className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Projects</h1>
              <p className="text-[13px] text-slate-500">
                {total != null
                  ? `${total} project${total !== 1 ? "s" : ""}`
                  : "Manage your inventory: projects, towers and units"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center rounded-lg border border-slate-200 p-0.5">
              <button
                type="button"
                onClick={() => setView("table")}
                aria-label="Table view"
                aria-pressed={view === "table"}
                className={`rounded-md p-1.5 ${view === "table" ? "bg-slate-100 text-slate-800" : "text-slate-400 hover:text-slate-600"}`}
              >
                <Table2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setView("cards")}
                aria-label="Card view"
                aria-pressed={view === "cards"}
                className={`rounded-md p-1.5 ${view === "cards" ? "bg-slate-100 text-slate-800" : "text-slate-400 hover:text-slate-600"}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
            <Button variant="default" size="sm" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </div>
        </div>

        {/* ── Filter rows ── */}
        <div className="space-y-2.5">
          <FilterChips label="Type" options={TYPE_FILTERS} value={projectType} onChange={setProjectType} />
          <FilterChips label="State" options={ACTIVE_FILTERS} value={active} onChange={setActive} />
          <div className="relative max-w-sm">
            <Search
              style={{ left: "0.75rem" }}
              className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              placeholder="Search name, code, city…"
              value={searchRaw}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── Results ── */}
        {loading && !data ? (
          view === "table" ? (
            <Skeleton className="h-72 w-full rounded-xl" />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-52 w-full rounded-xl" />
              ))}
            </div>
          )
        ) : projects.length === 0 ? (
          <Card className={`flex flex-col items-center justify-center gap-3 py-16 text-center ${LIGHT_CARD}`}>
            <Building2 className="h-10 w-10 text-slate-300" />
            <div>
              <p className="font-medium text-slate-700">No projects found</p>
              <p className="text-[13px] text-slate-500">
                Try clearing filters, or create a new project to start managing inventory.
              </p>
            </div>
            <Button size="sm" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </Card>
        ) : view === "table" ? (
          <ProjectsTable
            projects={projects}
            onView={handleView}
            onEdit={openEdit}
            onDelete={requestDelete}
            onAssign={handleAssign}
            assigningId={assigningId}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onView={handleView}
                onEdit={openEdit}
                onDelete={requestDelete}
                onAssign={handleAssign}
                assigning={assigningId === p.id}
              />
            ))}
          </div>
        )}
      </div>

      <ProjectDrawer
        open={drawerOpen}
        project={editing}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleSuccess}
      />

      <DeleteProjectModal
        open={!!deleting}
        project={deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        isDeleting={deleteHook.isPending}
      />
    </AppShell>
  )
}
